// REPANCY-AGENT-LOCK: agent-ux phase-K.10 (until 2026-05-25)
/**
 * K.10 — Pure helpers for rendering a `.md` driveItem's body across
 * the 3-view switcher used by RemoteDriveItemPreview (Rendered /
 * Markdown / Plain text). Mirror of the docx branch's helper trio
 * (mammoth.convertToHtml / turndown / mammoth.extractRawText) but
 * for markdown sources where the body is already markdown.
 *
 * Sanitization invariant: marked emits HTML strings with potential
 * inline scripts (footnotes, links, raw HTML in the source). We
 * never pass marked's output to dangerouslySetInnerHTML without
 * DOMPurify; the caller is responsible for the sanitize step (same
 * shape as the docx branch).
 */

import { marked } from 'marked';

/**
 * Render markdown source to HTML. Synchronous; `gfm: true` and
 * `breaks: false` to match GitHub-flavoured rendering.
 *
 * Note: marked.parse can be configured to return a Promise via
 * `async: true`. We stay synchronous so the React render pipeline
 * doesn't need a Suspense or useState dance.
 */
export function renderMarkdownToHtml(source: string): string {
  return marked.parse(source, {
    async: false,
    gfm: true,
    breaks: false,
  }) as string;
}

/**
 * Strip markdown formatting from a source string to produce a plain-
 * text view. Best-effort regex strip — not a full markdown AST walk.
 * Handles the common cases for the markdown branch:
 *   - ATX headings (# … ###### → text)
 *   - Bold/italic (*foo* / _foo_ / **foo** / __foo__)
 *   - Inline code (`foo`)
 *   - Fenced code blocks (```lang … ``` → preserve interior)
 *   - Links ([text](url) → text)
 *   - Images (![alt](url) → alt)
 *   - List markers (-, *, +, 1.)
 *   - Blockquote markers (>)
 *
 * Out of scope: tables, footnotes, setext headings, HTML passthrough.
 * Those render fine in the Rendered view; the Plain view is best-
 * effort for users who want to copy-paste prose.
 */
export function stripMarkdown(source: string): string {
  let out = source;

  // Fenced code blocks: strip the fence lines, keep the interior.
  out = out.replace(/^```[a-zA-Z0-9_-]*\n?/gm, '').replace(/^```\s*$/gm, '');

  // Images: ![alt](url) → alt
  out = out.replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1');

  // Links: [text](url) → text
  out = out.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

  // ATX headings: leading #+
  out = out.replace(/^#{1,6}\s+/gm, '');

  // Blockquote markers
  out = out.replace(/^>\s?/gm, '');

  // Unordered list markers (-, *, +)
  out = out.replace(/^[\s]*[-*+]\s+/gm, '');

  // Ordered list markers (1. 2. …)
  out = out.replace(/^[\s]*\d+\.\s+/gm, '');

  // Bold + italic: **foo**, __foo__, *foo*, _foo_
  out = out.replace(/\*\*([^*]+)\*\*/g, '$1');
  out = out.replace(/__([^_]+)__/g, '$1');
  out = out.replace(/\*([^*]+)\*/g, '$1');
  out = out.replace(/_([^_]+)_/g, '$1');

  // Inline code: `foo` → foo
  out = out.replace(/`([^`]+)`/g, '$1');

  // Collapse 3+ newlines to 2.
  out = out.replace(/\n{3,}/g, '\n\n');

  return out.trim();
}
